import React, { useMemo, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AlertManager from "../../shared/Alert/Alert";
import pageClasses from "./FlaggedItems.module.css";
import layoutClasses from "../LandedCostUpload/LandedCostUpload.module.css";

const SAMPLE_ROWS = [
  {
    id: 1,
    napaSku: "123-ABC",
    hqLine: "HQ-01",
    newLanded: "$25.45",
    updatedLanded: "$26.30",
    oldLanded: "$24.10",
    percentChange: "+5.6%",
    flaggedComment: "Review freight variance",
    flaggedBy: "Logistics Team",
    freight: "$3.40",
    tariff: "$1.25",
    demurrage: "$0.65",
    issCommission: "$0.55",
    defect: "$0.10",
    lastCalculated: "2024-06-02",
    runDetails: "Automated run – 06/02",
    linkedCjw: "CJW_2024_06_02.xlsx",
    linkedCostFile: "Cost_2024_06_02.xlsx",
  },
  {
    id: 2,
    napaSku: "456-DEF",
    hqLine: "HQ-02",
    newLanded: "$14.60",
    updatedLanded: "$14.95",
    oldLanded: "$14.20",
    percentChange: "+2.8%",
    flaggedComment: "Pending vendor confirmation",
    flaggedBy: "Merch Ops",
    freight: "$1.80",
    tariff: "$0.35",
    demurrage: "$0.22",
    issCommission: "$0.30",
    defect: "$0.05",
    lastCalculated: "2024-05-29",
    runDetails: "Manual run – 05/29",
    linkedCjw: "CJW_2024_05_29.xlsx",
    linkedCostFile: "Cost_2024_05_29.xlsx",
  },
];

const FlaggedItems = () => {
  const [rows, setRows] = useState(SAMPLE_ROWS);
  const [selectionModel, setSelectionModel] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [submitArmed, setSubmitArmed] = useState(false);
  const dirtyRowsRef = useRef(new Map());

  const columns = useMemo(
    () => [
      { field: "napaSku", headerName: "NAPA SKU", flex: 0.9, minWidth: 130, editable: true },
      { field: "hqLine", headerName: "HQ line", flex: 0.8, minWidth: 120, editable: true },
      { field: "newLanded", headerName: "New landed", flex: 0.9, minWidth: 140, editable: true },
      { field: "updatedLanded", headerName: "Updated_landed", flex: 0.9, minWidth: 160, editable: true },
      { field: "oldLanded", headerName: "Old landed", flex: 0.9, minWidth: 140, editable: true },
      { field: "percentChange", headerName: "% change", flex: 0.8, minWidth: 120, editable: true },
      { field: "flaggedComment", headerName: "Flagged comment", flex: 1.2, minWidth: 220, editable: true },
      { field: "flaggedBy", headerName: "Flagged by", flex: 0.9, minWidth: 150, editable: true },
      { field: "freight", headerName: "Freight", flex: 0.7, minWidth: 110, editable: true },
      { field: "tariff", headerName: "Tariff", flex: 0.7, minWidth: 110, editable: true },
      { field: "demurrage", headerName: "Demurrage", flex: 0.8, minWidth: 120, editable: true },
      { field: "issCommission", headerName: "ISS commision", flex: 0.8, minWidth: 140, editable: true },
      { field: "defect", headerName: "Defect", flex: 0.7, minWidth: 110, editable: true },
      { field: "lastCalculated", headerName: "Last calculated date", flex: 0.9, minWidth: 170, editable: true },
      { field: "runDetails", headerName: "Run details", flex: 1, minWidth: 180, editable: true },
      { field: "linkedCjw", headerName: "Linked CJW", flex: 0.9, minWidth: 160, editable: true },
      { field: "linkedCostFile", headerName: "Linked cost file", flex: 1, minWidth: 170, editable: true },
    ],
    []
  );

  const markRowAsDirty = (newRow, oldRow) => {
    if (!newRow || !oldRow) {
      return;
    }
    const changed = Object.keys(newRow).some((key) => newRow[key] !== oldRow[key]);
    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  };

  const handleProcessRowUpdate = (newRow, oldRow) => {
    if (!isEditing) {
      return oldRow;
    }
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === oldRow.id ? { ...row, ...newRow } : row))
    );
    markRowAsDirty(newRow, oldRow);
    return newRow;
  };

  const handleFinishEditing = () => {
    setSubmitArmed(true);
  };

  const handleEditToggle = () => {
    if (!isEditing) {
      dirtyRowsRef.current.clear();
      setIsEditing(true);
      setSubmitArmed(false);
      return;
    }
    if (!submitArmed) {
      return;
    }
    const payload = Array.from(dirtyRowsRef.current.values());
    dirtyRowsRef.current.clear();
    setIsEditing(false);
    setSubmitArmed(false);
    if (payload.length) {
      AlertManager.success("Flagged items updated.", { contactSupport: false });
    }
  };

  return (
    <section className={pageClasses.page}>
      <header className={pageClasses.header}>
        <div>
          <h1>Flagged Items</h1>
          <p>Review flagged SKUs with the latest landed cost calculations.</p>
        </div>
      </header>

      <div className={layoutClasses.resultsTableCard}>
        <Box className={layoutClasses.resultsActionBar}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleEditToggle}
            disabled={isEditing && !submitArmed}
          >
            {isEditing ? "Save Changes" : "Edit"}
          </Button>
          {isEditing && (
            <Button
              variant="text"
              size="small"
              className={layoutClasses.finishEditingBtn}
              onClick={handleFinishEditing}
            >
              Finish Editing
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={!selectionModel.length || isEditing}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            disabled={!selectionModel.length || isEditing}
          >
            Reject
          </Button>
        </Box>

        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          checkboxSelection
          disableRowSelectionOnClick
          editMode="cell"
          isCellEditable={() => isEditing}
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => console.error(error)}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) => {
            const ids = Array.isArray(newSelection)
              ? newSelection
              : Array.from(newSelection || []);
            setSelectionModel(ids);
          }}
          hideFooterPagination
        />
      </div>
    </section>
  );
};

export default FlaggedItems;
